// Tiny synth-based "whisper arrived" notification chime. No external audio
// assets — we generate a short metallic two-note "ting" with the Web Audio
// API so it ships zero bytes over the network and works offline.
//
// Browsers (especially mobile Safari) require an AudioContext to be created
// inside a user gesture before audio can play, so we lazy-create + unlock
// the context on the first click anywhere on the page after this module is
// imported.

const MUTE_KEY = "drr_whisper_sound_muted";
let ctx = null;
let unlocked = false;

function getContext() {
  if (ctx) return ctx;
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
    return ctx;
  } catch (e) {
    return null;
  }
}

// Unlock on the first user gesture so subsequent programmatic plays succeed.
// Safe to call multiple times — the listener self-removes after firing once.
function attachUnlockListener() {
  if (unlocked || typeof window === "undefined") return;
  const handler = () => {
    const c = getContext();
    if (c && c.state === "suspended") c.resume().catch(() => {});
    unlocked = true;
    window.removeEventListener("pointerdown", handler);
    window.removeEventListener("keydown", handler);
  };
  window.addEventListener("pointerdown", handler, { once: true });
  window.addEventListener("keydown", handler, { once: true });
}

if (typeof window !== "undefined") attachUnlockListener();

export function isWhisperSoundMuted() {
  try {
    return localStorage.getItem(MUTE_KEY) === "1";
  } catch (e) {
    return false;
  }
}

export function setWhisperSoundMuted(muted) {
  try {
    if (muted) localStorage.setItem(MUTE_KEY, "1");
    else localStorage.removeItem(MUTE_KEY);
  } catch (e) {
    /* ignore */
  }
}

// Play a short two-note metallic "ting". Returns silently if muted, if the
// AudioContext hasn't been unlocked yet, or if Web Audio is unsupported.
export function playWhisperPing() {
  if (isWhisperSoundMuted()) return;
  const c = getContext();
  if (!c) return;
  if (c.state === "suspended") {
    // Try to resume — works after the first user gesture has fired
    c.resume().catch(() => {});
    if (c.state === "suspended") return;
  }
  const now = c.currentTime;
  // Two slightly-detuned notes (E6 + B5) → bright metal chime
  const notes = [
    { freq: 1318.51, start: 0,    decay: 0.45 },
    { freq:  987.77, start: 0.08, decay: 0.55 },
  ];
  // Master gain so the chime is unobtrusive (~ -18 dB peak)
  const master = c.createGain();
  master.gain.value = 0.12;
  master.connect(c.destination);

  for (const n of notes) {
    const osc = c.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = n.freq;
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, now + n.start);
    g.gain.exponentialRampToValueAtTime(0.9, now + n.start + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, now + n.start + n.decay);
    osc.connect(g).connect(master);
    osc.start(now + n.start);
    osc.stop(now + n.start + n.decay + 0.05);
  }
}
